import BaseRepositoryService from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import type BookingDotComLinkAdapter from 'uplisting-frontend/adapters/booking-dot-com-link';
import type { IEligibleRatePlan } from 'uplisting-frontend/models/booking-dot-com-link';
import { BookingDotComLinkValidation } from 'uplisting-frontend/validations';

export interface IReconcileRatePlanError {
  code: string;
  detail?: string;
}

interface IReconcileRatePlanFailure {
  errors?: {
    code?: string;
    detail?: string;
    meta?: { eligible_rate_plans?: IEligibleRatePlan[] };
  }[];
}

export function reconcileRatePlanError(
  error: unknown,
): IReconcileRatePlanError | undefined {
  const first = (error as IReconcileRatePlanFailure)?.errors?.[0];

  if (!first?.code) {
    return undefined;
  }

  return { code: first.code, detail: first.detail };
}

export default class BookingDotComLinkRepositoryService extends BaseRepositoryService<
  ModelRegistry['booking-dot-com-link']
> {
  recordName = 'booking-dot-com-link';
  implementMethods = ['buildChangeset', 'createRecord'];

  validation = BookingDotComLinkValidation;

  public async reconcileRatePlan(
    link: ModelRegistry['booking-dot-com-link'],
    rateId?: string,
  ): Promise<void> {
    try {
      const adapter = this.store.adapterFor(
        'booking-dot-com-link',
      ) as BookingDotComLinkAdapter;
      const payload = await adapter.reconcileRatePlan(link.id, rateId);

      this.store.pushPayload(payload);
    } catch (err) {
      const error = reconcileRatePlanError(err);
      const refreshed = (err as IReconcileRatePlanFailure)?.errors?.[0]?.meta
        ?.eligible_rate_plans;

      if (error?.code === 'room_not_found') {
        try {
          await link.reload();
        } catch (reloadError) {
          this.sentry.captureException(reloadError);
        }
      } else if (refreshed) {
        link.eligibleRatePlans = refreshed;
      }

      if (!error) {
        this.sentry.captureException(err, {
          tags: { repository: this.recordName, operation: 'reconcileRatePlan' },
        });
      }

      throw err;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'repositories/booking-dot-com-link': BookingDotComLinkRepositoryService;
  }
}
